import React, {useEffect, useContext, useState, useCallback} from 'react';
import {Context} from '../context';
import Button from './UI/Button';
import Section from './UI/Section';
import {clone} from 'ramda';
import {
  dollarAmount,
  getDisplayItemTextPhraseFromSlug,
  isAPassNotATicket
} from '../helperFunctions';

const selectTitleClassName = 'text-md font-semibold w-full min-w-fit mt-6 mb-2';
const selectClassName = 'text-lg w-full min-w-fit border rounded-md p-3';

function ProductSelector2() {
  const {
    account,
    addToCartPasses,
    localeText,
    content,
    setSnackBarMessage,
    getTicketName
  } = useContext(Context);

  const [nodes, setNodes] = useState([]);
  const [selections, setSelections] = useState([]);
  const [product, setProduct] = useState(null);

  const ucwords = str =>
    (str.toLowerCase() + '').replace(/^([a-z])|\s+([a-z])/g, a =>
      a.toUpperCase()
    );

  useEffect(() => {
    setNodes(content?.menu?.nodes);
  }, [content]);

  const getNode = nodeKey => {
    return nodes.find(node => node.key === nodeKey);
  };

  const selectionDeepCloneTrimmedBeforeStep = (
    selectionStep,
    defaultSelected
  ) => {
    const newSelection = [];
    for (const selection of selections) {
      if (selection.selectionStep === selectionStep) {
        if (defaultSelected) {
          newSelection.push(clone(selection));
        }
        break;
      }
      newSelection.push(clone(selection));
    }
    return newSelection;
  };

  const getSelectionStep = node => {
    if (node.transitCards) {
      return 'transitCard';
    } else if (node.items) {
      if (node.key === 'root') return 'rider';
      else return 'fare';
    } else if (node.metaName) {
      return node.metaName;
    } else return null;
  };

  const getLastNode = useCallback(() => {
    if (selections.length > 0) return selections[selections.length - 1];
    return [];
  }, [selections]);

  // after making selection, reset the following selection to default value
  useEffect(() => {
    const lastNode = getLastNode();
    let lastElement = document.getElementById(`menu-${lastNode.key}`);
    if (lastElement?.value) lastElement.value = 'default';
  }, [selections, getLastNode]);

  const resetSelections = useCallback(() => {
    setSelections([
      {
        selectionStep: 'transitCard',
        transitCards: account?.transitAccounts[0]?.transitCards
      }
    ]);

    const transitCardInput = document.getElementById('menu-transitCards');
    if (transitCardInput) {
      transitCardInput.value = 'default';
    }
  }, [setSelections, account]);

  //* 0 - Initial node -> Transit Cards
  useEffect(() => {
    if (selections.length === 0) resetSelections();
  }, [account, resetSelections, selections.length]);

  const selectionHandler = (element, nodeCur) => {
    let nodeNex;
    let newSelection;

    if (element.target.value === 'default') {
      const defaultSelected = true;
      newSelection = selectionDeepCloneTrimmedBeforeStep(
        nodeCur.selectionStep,
        defaultSelected
      );
    } else {
      if (getSelectionStep(nodeCur) === 'transitCard') {
        nodeCur.selectedTransitCard = //set transitCard
          account?.transitAccounts[0]?.transitCards.find(
            card => card.uuid === element.target.value
          );
        nodeNex = content?.menu?.nodes.find(node => node.key === 'root');
        nodeNex.selectionStep = 'rider';
        newSelection = selectionDeepCloneTrimmedBeforeStep('rider');
      } else if (getSelectionStep(nodeCur) === 'fare') {
        nodeNex = clone(getNode(element.target.value));
        nodeNex.selectionStep = 'origin';
        newSelection = selectionDeepCloneTrimmedBeforeStep('origin');
      } else if (getSelectionStep(nodeCur) === 'origin') {
        nodeNex = getNode(nodeCur.next);
        nodeNex.selectionStep = 'destination';
        newSelection = selectionDeepCloneTrimmedBeforeStep('destination');
        const newNodeCur = newSelection.find(node => node.key === nodeCur.key);
        newNodeCur.station = element.target.value;
      } else if (getSelectionStep(nodeCur) === 'destination') {
        nodeNex = getNode(nodeCur.next);
        nodeCur.station = element.target.value;
        newSelection = selectionDeepCloneTrimmedBeforeStep();
        const newNodeCur = newSelection.find(node => node.key === nodeCur.key);
        newNodeCur.destination = element.target.value;
      }
      newSelection.push(nodeNex);
    }

    if (
      getSelectionStep(nodeCur) === 'transitCard' &&
      element.target.value !== 'default'
    ) {
      const transitCardRiderType =
        nodeCur.selectedTransitCard?.riderType?.toLowerCase();
      const nodeNexNex = getNode(transitCardRiderType);
      nodeNexNex.selectionStep = 'fare';
      newSelection.push(nodeNexNex);
    }

    setSelections(newSelection);
  };

  useEffect(() => {
    // check if at final step by checking if element contains a "map" field
    const end = selections.find(selection => selection.map);
    if (end) {
      //* if product found & includes Origin/Destination
      const originStation = selections.find(
        selection => selection.metaName === 'origin'
      ).station;
      const destinationStation = selections.find(
        selection => selection.metaName === 'destination'
      ).station;

      const productKey = end.map.find(
        e =>
          (e[0] === originStation && e[1] === destinationStation) ||
          (e[1] === originStation && e[0] === destinationStation)
      )[2];

      setProduct(() => {
        const curProduct = clone(nodes.find(node => node.key === productKey));

        // Add Station IDs
        const getStationIdFromKey = stationKey => {
          const stationNode = nodes.find(node => node.key === stationKey);
          return stationNode ? stationNode.metaValue : null;
        };

        const origStnNode = selections.find(s => s.metaName === 'origin');
        const origStnKey = origStnNode ? origStnNode.station : null;
        const desStnNode = selections.find(s => s.metaName === 'destination');
        const destStnKey = desStnNode ? desStnNode.station : null;

        curProduct.origin_id = getStationIdFromKey(origStnKey);
        curProduct.destination_id = getStationIdFromKey(destStnKey);

        return curProduct;
      });
    } else if (getLastNode()?.key?.includes('step_up')) {
      //* if product found & is type Step Up Zone
      setProduct(clone(nodes.find(node => node.key === getLastNode().key)));
    } else {
      //* if No product is found
      setProduct(null);
    }
  }, [selections, getLastNode, nodes]);

  const getCorrectOrder = items => {
    if (items.includes('debary')) {
      const deland_key = 'de_land_amtrak';
      return [
        ...[
          content.menu.nodes.find(node => node.key === deland_key) && deland_key
        ],
        'debary',
        'sanford',
        'lake_mary',
        'longwood',
        'altamonte_springs',
        'maitland',
        'winter_park',
        'adventhealth',
        'lynx_central',
        'church_street',
        'orlando_health',
        'sand_lake_road',
        'meadow_woods',
        'tupperware',
        'kissimmee',
        'poinciana'
      ];
    }
    return items;
  };

  const add = () => {
    const nodeTransitCard = selections.find(
      selection => selection.selectionStep === 'transitCard'
    );
    addToCartPasses({
      id: product.id,
      key: product.key,
      quantity: 1,
      price: product.price,
      uuid: nodeTransitCard?.selectedTransitCard?.uuid,
      origin_id: product.origin_id,
      destination_id: product.destination_id
    });

    setSnackBarMessage(
      localeText(
        'rw_purchasepasses_text_item_added_to_cart',
        'Item added to cart'
      )
    );

    resetSelections();
  };

  return (
    <Section
      heading={localeText('rw_purchasepasses_header', 'Product Selector')}
    >
      {selections.map(selection => {
        if (selection.transitCards) {
          //* 1 - TransitCard ( RiderType (2) auto selected)
          return (
            <React.Fragment key={selection.selectionStep}>
              <div className={selectTitleClassName}>
                {localeText(
                  'rw_purchasepasses_title_select_a_transit_card',
                  'Select a Transit Card'
                )}
              </div>
              <select
                key='transitCards'
                name=''
                id='menu-transitCards'
                onChange={e => selectionHandler(e, selection)}
                className={selectClassName}
              >
                <option value='default'>
                  {' '}
                  --{' '}
                  {localeText(
                    'rw_purchasepasses_title_select_a_transit_card',
                    'Select a Transit Card'
                  )}
                </option>
                {selection.transitCards?.map(tc => {
                  return (
                    <option key={tc.uuid} value={tc.uuid}>
                      {tc.nickname} (
                      {localeText(tc.riderType, ucwords(tc.riderType))})
                    </option>
                  );
                })}
              </select>
            </React.Fragment>
          );
        } else if (selection.selectionStep === 'fare') {
          //* 3 - FareType
          return (
            <React.Fragment key={selection.selectionStep}>
              <div className={selectTitleClassName}>
                {localeText(
                  'rw_purchasepasses_title_choose_fare',
                  'Choose your Fare'
                )}
              </div>
              <select
                key={selection.key}
                name=''
                id={`menu-${selection.key}`}
                onChange={e => selectionHandler(e, selection)}
                className={selectClassName}
              >
                <option value='default'>
                  {`--
                  ${localeText(
                    'rw_purchasepasses_title_choose_fare',
                    'Choose your Fare'
                  )}`}
                </option>
                {selection.items
                  .filter(ticket => isAPassNotATicket(ticket))
                  .map(nodeKey => (
                    <option key={nodeKey} value={nodeKey}>
                      {
                        // origin name
                        getDisplayItemTextPhraseFromSlug(
                          localeText,
                          content,
                          getNode(nodeKey).displayItem
                        )
                      }
                    </option>
                  ))}
              </select>
            </React.Fragment>
          );
        } else if (selection.itemsRef) {
          //* 4 & 5 - Origin & Destination
          const node = getNode(selection.itemsRef);
          return (
            <React.Fragment key={selection.selectionStep}>
              <div className={selectTitleClassName}>
                {getSelectionStep(selection) === 'origin' &&
                  localeText(
                    'rw_purchasepasses_title_choose_origin',
                    'Choose your Origin'
                  )}
                {getSelectionStep(selection) === 'destination' &&
                  localeText(
                    'rw_purchasepasses_title_choose_destination',
                    'Choose your Destination'
                  )}
              </div>

              <select
                key={selection.key}
                name=''
                id={`menu-${selection.key}`}
                onChange={e => selectionHandler(e, selection)}
                className={selectClassName}
              >
                <option value='default'>
                  {`--
                  ${
                    getSelectionStep(selection) === 'origin'
                      ? localeText(
                          'rw_purchasepasses_title_choose_origin',
                          'Choose your Origin'
                        )
                      : localeText(
                          'rw_purchasepasses_title_choose_destination',
                          'Choose your Destination'
                        )
                  }`}
                </option>
                {getCorrectOrder(node.items)
                  .filter(option => {
                    if (selection.selectionStep === 'destination') {
                      const originNode = selections.find(
                        node => node.selectionStep === 'origin'
                      );
                      if (option !== originNode.station) return option;
                      else return null;
                    } else return option;
                  })
                  .map(nodeKey => (
                    <option key={nodeKey} value={nodeKey}>
                      {
                        // destination name
                        getDisplayItemTextPhraseFromSlug(
                          localeText,
                          content,
                          getNode(nodeKey).displayItem
                        )
                      }
                    </option>
                  ))}
              </select>
            </React.Fragment>
          );
        } else return null;
      })}
      {product && (
        <div className='flex flex-col my-6'>
          <div className='text-lg font-semibold'>
            {getTicketName(product.id)}
          </div>
          <div className='text-lg font-bold'>{dollarAmount(product.price)}</div>
          <Button className='w-fit self-end' onClick={add}>
            {localeText('rw_purchasepasses_button_add_to_cart', 'Add to Cart')}
          </Button>
        </div>
      )}
    </Section>
  );
}

export default ProductSelector2;
